import React, { useState, useEffect } from "react";
import { Card, Form, Alert, Spinner, Button, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { ProductFormValues } from "../types";
import { themeColors } from "./ProductAttributeFieldStyles";

interface ProductVideoFieldProps {
  formValues: ProductFormValues;
  onChange: (field: string, value: any) => void;
  invalidFields?: string[];
  readOnly?: boolean;
}

const ProductVideoField: React.FC<ProductVideoFieldProps> = ({
  formValues,
  onChange,
  invalidFields = [],
  readOnly = false,
}) => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [isValidUrl, setIsValidUrl] = useState<boolean>(true);
  const [isLoadingVideoInfo, setIsLoadingVideoInfo] = useState<boolean>(false);
  const [autoPreviewImage, setAutoPreviewImage] = useState<string | null>(null);

  // Available video roles (same as image roles)
  const availableRoles = [
    { value: "image", label: t("products.roles.image", "Base Image") },
    { value: "small_image", label: t("products.roles.small_image", "Small Image") },
    { value: "thumbnail", label: t("products.roles.thumbnail", "Thumbnail") },
    { value: "swatch_image", label: t("products.roles.swatch_image", "Swatch Image") }
  ];

  // YouTube URL validation
  const validateYouTubeUrl = (url: string): boolean => {
    if (!url) return true; // Empty URL is valid (optional field)
    const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/;
    return pattern.test(url);
  };

  // Extract YouTube video ID from URL
  const extractYouTubeVideoId = (url: string): string | null => {
    if (!url) return null;
    
    const patterns = [
      /youtube\.com\/watch\?v=([^&?/]+)/,
      /youtube\.com\/embed\/([^&?/]+)/,
      /youtu\.be\/([^&?/]+)/,
      /youtube\.com\/v\/([^&?/]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  // Fetch video information from YouTube API
  const fetchVideoInfo = async (videoId: string) => {
    if (readOnly) return; // Don't fetch info in read-only mode
    
    setIsLoadingVideoInfo(true);
    try {
      // Use YouTube oEmbed API to get video information
      const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
      if (response.ok) {
        const data = await response.json();
        
        // Auto-fill title if not already set
        if (!formValues.video_title && data.title) {
          onChange("video_title", data.title);
        }
        
        // Auto-fill description if not already set (YouTube oEmbed doesn't provide description, but we can set a default)
        if (!formValues.video_description && data.title) {
          onChange("video_description", `Watch this video about ${data.title}`);
        }
      }
    } catch (error) {
      console.warn("Could not fetch video information:", error);
    } finally {
      setIsLoadingVideoInfo(false);
    }
  };

  // Update video preview when URL changes
  useEffect(() => {
    const videoUrl = formValues.video_url || "";
    const isValid = validateYouTubeUrl(videoUrl);
    setIsValidUrl(isValid);
    
    if (isValid && videoUrl) {
      const videoId = extractYouTubeVideoId(videoUrl);
      if (videoId) {
        setVideoPreview(`https://www.youtube.com/embed/${videoId}`);
        // Set auto preview image from YouTube thumbnail
        const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
        setAutoPreviewImage(thumbnailUrl);
        // Auto-set preview image if not already set and not in read-only mode
        if (!formValues.video_preview_image && !readOnly) {
          onChange("video_preview_image", thumbnailUrl);
        }
        // Auto-fetch video information
        fetchVideoInfo(videoId);
      } else {
        setVideoPreview(null);
        setAutoPreviewImage(null);
      }
    } else {
      setVideoPreview(null);
      setAutoPreviewImage(null);
    }
  }, [formValues.video_url, readOnly]);

  const handleVideoUrlChange = (value: string) => {
    if (!readOnly) {
      onChange("video_url", value);
    }
  };

  const handleVideoTitleChange = (value: string) => {
    if (!readOnly) {
      onChange("video_title", value);
    }
  };

  const handleVideoDescriptionChange = (value: string) => {
    if (!readOnly) {
      onChange("video_description", value);
    }
  };

  const isVideoUrlInvalid = invalidFields.includes("video_url") || (!!formValues.video_url && !isValidUrl);

  // If no video data exists and in read-only mode, don't render the component
  if (readOnly && !formValues.video_url) {
    return null;
  }

  return (
    <Card className="mb-4">
      <Card.Header className="bg-body-tertiary">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon 
            icon={faVideo} 
            className="me-2" 
            style={{ color: themeColors.orange }}
          />
          <h6 className="mb-0" style={{ textAlign: isArabic ? "right" : "left" }}>
            {t("products.video.title", "Product Video")}
            {readOnly && <span className="ms-2 badge bg-secondary">{t("readOnly", "Read Only")}</span>}
          </h6>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="row g-3">
          {/* Video URL Field */}
          <div className="col-12">
            <Form.Group>
              <Form.Label className="fw-semibold">
                {t("products.video.url", "YouTube Video URL")}
                {!readOnly && <span className="text-muted ms-1">({t("optional", "Optional")})</span>}
              </Form.Label>
              <Form.Control
                type="url"
                placeholder={readOnly ? "" : t("products.video.urlPlaceholder", "https://www.youtube.com/watch?v=...")}
                value={formValues.video_url || ""}
                onChange={(e) => handleVideoUrlChange(e.target.value)}
                isInvalid={!readOnly && isVideoUrlInvalid}
                readOnly={readOnly}
                disabled={readOnly}
                style={{
                  textAlign: isArabic ? "right" : "left",
                  direction: isArabic ? "rtl" : "ltr"
                }}
              />
              {!readOnly && isVideoUrlInvalid && (
                <Form.Control.Feedback type="invalid">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                  {t("products.video.invalidUrl", "Please enter a valid YouTube URL")}
                </Form.Control.Feedback>
              )}
              {!readOnly && (
                <Form.Text className="text-muted">
                  {t("products.video.urlHelp", "Add a YouTube video to showcase your product. Supported formats: youtube.com/watch?v=, youtu.be/, youtube.com/embed/")}
                </Form.Text>
              )}
            </Form.Group>
          </div>

          {/* Video Preview */}
          {videoPreview && isValidUrl && (
            <div className="col-12">
              <div className="video-preview-container">
                <h6 className="mb-2">{t("products.video.preview", "Video Preview")}</h6>
                <div 
                  className="ratio ratio-16x9 mb-3"
                  style={{ 
                    maxWidth: "400px",
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    overflow: "hidden"
                  }}
                >
                  <iframe
                    src={videoPreview}
                    title={t("products.video.previewTitle", "Product Video Preview")}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          )}

          {/* Video Title Field */}
          <div className="col-12">
            <Form.Group>
              <Form.Label className="fw-semibold">
                {t("products.video.title", "Video Title")}
                {!readOnly && <span className="text-muted ms-1">({t("optional", "Optional")})</span>}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={readOnly ? "" : t("products.video.titlePlaceholder", "Enter a title for your video")}
                value={formValues.video_title || ""}
                onChange={(e) => handleVideoTitleChange(e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                style={{
                  textAlign: isArabic ? "right" : "left",
                  direction: isArabic ? "rtl" : "ltr"
                }}
              />
              {!readOnly && (
                <Form.Text className="text-muted">
                  {t("products.video.titleHelp", "A descriptive title for your product video")}
                </Form.Text>
              )}
            </Form.Group>
          </div>

          {/* Video Description Field */}
          <div className="col-12">
            <Form.Group>
              <Form.Label className="fw-semibold">
                {t("products.video.description", "Video Description")}
                {!readOnly && <span className="text-muted ms-1">({t("optional", "Optional")})</span>}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder={readOnly ? "" : t("products.video.descriptionPlaceholder", "Describe what customers will see in this video")}
                value={formValues.video_description || ""}
                onChange={(e) => handleVideoDescriptionChange(e.target.value)}
                readOnly={readOnly}
                disabled={readOnly}
                style={{
                  textAlign: isArabic ? "right" : "left",
                  direction: isArabic ? "rtl" : "ltr"
                }}
              />
              {!readOnly && (
                <Form.Text className="text-muted">
                  {t("products.video.descriptionHelp", "Additional details about your product video content")}
                </Form.Text>
              )}
            </Form.Group>
          </div>

          {/* Auto Preview Image Section - Only show YouTube thumbnail */}
          {formValues.video_url && isValidUrl && autoPreviewImage && (
            <div className="col-12">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  {t("products.video.previewImage", "Video Preview Image")}
                  <span className="text-muted ms-1">({t("optional", "Optional")})</span>
                </Form.Label>
                
                {readOnly ? (
                  // Read-only mode: just show the current preview image
                  <div className="mt-2">
                    {formValues.video_preview_image && (
                      <img 
                        src={formValues.video_preview_image} 
                        alt="Video Preview"
                        className="img-fluid rounded"
                        style={{ maxHeight: "200px", objectFit: "cover" }}
                      />
                    )}
                  </div>
                ) : (
                  // Edit mode: show YouTube thumbnail only
                  <div className="border rounded p-3">
                    <h6 className="mb-2">{t("products.video.autoPreview", "YouTube Thumbnail")}</h6>
                    <img 
                      src={autoPreviewImage} 
                      alt="YouTube Thumbnail"
                      className="img-fluid rounded mb-2"
                      style={{ maxHeight: "150px", objectFit: "cover" }}
                    />
                
                    <Form.Text className="d-block mt-2 text-muted">
                      {t("products.video.autoPreviewHelp", "Preview image is automatically generated from YouTube thumbnail")}
                    </Form.Text>
                  </div>
                )}
              </Form.Group>
            </div>
          )}

          {/* Video Roles Selection */}
          {formValues.video_url && isValidUrl && (
            <div className="col-12">
              <Form.Group>
                <Form.Label className="fw-semibold">
                  {t("products.video.roles", "Video Roles")}
                  {!readOnly && <span className="text-muted ms-1">({t("optional", "Optional")})</span>}
                </Form.Label>
                {readOnly ? (
                  // Read-only mode: show selected roles as badges
                  <div className="mt-2">
                    {formValues.video_roles && formValues.video_roles.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {formValues.video_roles.map((role) => {
                          const roleLabel = availableRoles.find(r => r.value === role)?.label || role;
                          return (
                            <span key={role} className="badge bg-primary">
                              {roleLabel}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-muted">{t("products.video.noRoles", "No roles selected")}</span>
                    )}
                  </div>
                ) : (
                  // Edit mode: show checkboxes
                  <>
                    <div className="row g-2">
                      {availableRoles.map((role) => (
                        <div key={role.value} className="col-md-6 col-lg-3">
                          <Form.Check
                            type="checkbox"
                            id={`video-role-${role.value}`}
                            label={role.label}
                            checked={formValues.video_roles?.includes(role.value) || false}
                            onChange={(e) => {
                              const currentRoles = formValues.video_roles || [];
                              if (e.target.checked) {
                                onChange("video_roles", [...currentRoles, role.value]);
                              } else {
                                onChange("video_roles", currentRoles.filter(r => r !== role.value));
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <Form.Text className="text-muted">
                      {t("products.video.rolesHelp", "Select where this video should be displayed (same as image roles)")}
                    </Form.Text>
                  </>
                )}
              </Form.Group>
            </div>
          )}

          {/* Info Alert - only show in edit mode */}
          {!readOnly && (
            <div className="col-12">
              <Alert variant="info" className="mb-0">
                <div className="d-flex align-items-start">
                  <FontAwesomeIcon icon={faVideo} className="me-2 mt-1" />
                  <div>
                    <strong>{t("products.video.infoTitle", "Video Guidelines:")}</strong>
                    <ul className="mb-0 mt-2">
                      <li>{t("products.video.guideline1", "Only YouTube videos are supported")}</li>
                      <li>{t("products.video.guideline2", "Video should showcase your product features")}</li>
                      <li>{t("products.video.guideline3", "Ensure video content complies with platform policies")}</li>
                      <li>{t("products.video.guideline4", "Video will be displayed alongside product images")}</li>
                    </ul>
                  </div>
                </div>
              </Alert>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ProductVideoField;
